import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  subMonths, endOfMonth, subYears, addDays, startOfDay,
} from 'date-fns';
import { PropTypes } from 'prop-types';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { withStyles } from '@mui/styles';
import { Card, CardHeader, CardContent, Typography, Dialog, DialogContent } from '@mui/material';

import { LoadingView, ConfirmationDialog, Footer } from 'components';
import { StoreUtil } from 'doctivity-shared/utils';
import { withRouter, withIsMobile, withClientQuery } from 'utils';
import { setDateRange, closeAiLabelFilter } from 'store/actions/appActions';
import {
  UsersPage,
  UserPage,
  ActivityLogPage,
  UsageAnalyticsPage,
  DevRequestsPage,
  ClientsPage,
  ProvidersPage,
  AffiliatedProvidersPage,
  ReferringProvidersPage,
  ProviderDetailPage,
  UploadPage,
  TaxonomyPage,
  ProceduresPage,
  QueryPage,
  ReferralFlowPage,
  VisitsPage,
  RVUBenchmarksPage,
  ProviderCommentsPageContainer,
  RecentProvidersPage,
  FavoriteProvidersPage,
  LoginSettingsPage,
  CustomServiceLinesPage,
  QAPage,
  ReportBuilderPage,
  AnnouncementsPage,
  NotificationsPageContainer,
} from 'containers/pages';
import {
  DashboardV2Page,
} from 'dashboard';
import {
  OrganizationsPage,
  OrganizationDetailPage,
  RecentOrganizationsPage,
  AffiliatedOrganizationsPage,
  FavoriteOrganizationsPage,
  PatientMigrationPage,
} from 'organizations';
import {
  TagNamespacesPage,
  TagsPage as LabelTypesPage,
  TagDetailsPage,
} from 'tags';
import { ContactsPage, ContactDetailPage, FavoriteContactsPage, RecentContactsPage} from 'contacts';
import { DocumentsPage } from 'documents';
import { hideConfirmationDialog } from 'store/actions/systemActions';
import { listClients } from 'store/actions/clientsActions';
import { initUsageBeacon, setUsageContext, trackPageView } from 'components/usage/usageBeacon';
import { Header } from './Header';
import { AppNavigation } from './AppNavigation';
import {
  LocationsPage,
  LocationDetailPage,
  RecentLocationsPage,
  FavoriteLocationsPage,
  LocationChangesPage,
  LocationsMapPage
} from 'locations';
import { ActivitySnackbar, ClaimsTabs } from 'components';
import UpgradeableContext from './UpgradeableContext';
import { ChatBotContext } from './ChatBotContext';
import SupportIcon from 'components/SupportIcon';
import ErrorBoundary from 'components/ErrorBoundary';

import AiProviderFilter from 'components/chatbot/AiProviderFilter';
import { listProviderTagNamespaces, API_TAG_NAMESPACES_PROVIDERS_LIST } from 'store/actions/tagNamespacesActions';

const drawerWidth = 224;
const minimizedDrawerWidth = 100;

const styles = (theme) => ({
  root: {
    display: 'flex',
    fontFamily: 'sans-serif',
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
    backgroundColor: '#fafafa',
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      left: minimizedDrawerWidth,
      right: 0,
    },
  },
  container: {
    padding: 0,
    minHeight: '80%', // enough to get footer down
  },
  cardContainer: {
    marginTop: 200,
    marginLeft: 200,
    width: 350,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  button: {
    width: 150,
  },
  cardContent: {
    marginTop: 0,
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  link: {
    color: '#00A887',
  },
  listSubheader: {
    backgroundColor: '#00A887',
    color: '#fff',
  },
  selectedWedgeIcon: {
    color: '#FAFAFA',
    marginRight: -17,
    fontSize: 40,
    position: 'absolute',
    right: 0,
  },
  divider: {
    borderColor: '#05BE8D',
  },
  accountIcon: {
    height: 16,
    width: 16,
    marginLeft: -20,
    paddingRight: 4,
    color: theme.palette.primary.bright,
  },
  logout: {
    '&:hover': {
      textDecoration: 'underline',
    },
    color: '#FFFFFF',
  },
  clientContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: minimizedDrawerWidth - 5 * parseInt(theme.spacing()),
    height: 24,
    backgroundColor: theme.palette.secondary.accent,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    padding: theme.spacing(),
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: theme.spacing(2),
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.shorter,
    }),
  },
  clientIcon: {
    fontSize: 12,
    fontWeight: 300,
    marginLeft: theme.spacing(),
    color: theme.palette.primary.accent,
  },
  clientName: {
    fontSize: 12,
    color: theme.palette.primary.accent,
  },
  clientContainerOpen: {
    maxWidth: drawerWidth - 5 * parseInt(theme.spacing()),
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  clientListItem: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: 0,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
    color: theme.palette.primary.accent,
  },
  listItemText: {
    marginLeft: -16,
  },
  userNameContainer: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    width: minimizedDrawerWidth - 20,
  },
  userNameContainerLong: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    width: minimizedDrawerWidth + 40,
  },
  userName: {
    fontSize: 11,
    textAlign: 'center',
  },
});

// Redirects the retired /qa URLs to their /testing equivalents, carrying the
// tab segment across so /qa/labels lands on /testing/labels.
function LegacyQaRedirect() {
  const { tab } = useParams();
  return <Navigate to={tab ? `/testing/${tab}` : '/testing'} replace />;
}

class AppRoot extends Component {

  constructor(props) {
    super(props);

    this.state = {
      clientMenuOpen: false,
      waitingForMouseExit: false,
      openChat: () => {},   // will be supplied by AppNavigation
      clicked : false,      // support-icon opacity (shared)
    };
  }

  /* handler AppNavigation will call once during mount */
  registerOpenChat = (fn) => {
    this.setState({ openChat: fn });
  };

  /* called by every SupportIcon to update the clicked flag */
  setClicked = (v) => {
    if (v !== this.state.clicked) this.setState({ clicked: v });
  };


  static contextType = UpgradeableContext;

  componentDidMount() {
    const { user, clients, dispatch } = this.props;

    if (user && user.id) {
      if (StoreUtil.needsLoadNoCache(clients)) {
        dispatch(listClients());
      }
      this.setDefaultDate();
    }

    // First-party page-view beacon: start tracking the initial page.
    initUsageBeacon();
    setUsageContext(user && user.id, this.props.selectedClient);
    trackPageView(this.props.router.location.pathname);
  }

  componentDidUpdate(props) {
    if (this.props.router.location.pathname != props.router.location.pathname) {
      // When navigating to a labels page, inject label context into audio conversation
      const newPath = this.props.router.location.pathname;
      trackPageView(newPath);
      const labelMatch = newPath.match(/^\/labels\/(\d+)/);
      if (labelMatch) {
        const tagId = parseInt(labelMatch[1], 10);
        // Find tag and namespace from Redux store
        const allTags = this.props.tags?.data || [];
        const allNamespaces = Object.values(this.props.tagNamespaces || {}).flatMap(s => s?.data || []);
        const tag = allTags.find(t => t.id === tagId);
        const namespace = tag ? allNamespaces.find(n => n.id === tag.namespace_id) : null;
        if (tag && namespace) {
          window.dispatchEvent(new CustomEvent('label-context-changed', {
            detail: { tagId, tagName: tag.name, namespaceName: namespace.name }
          }));
        }
      }
      if (this.context && this.context.callNavigationHandler) {
        this.context.callNavigationHandler();
      }
    }
    
    if (
      this.props.user &&
      (!props.user || this.props.user.id !== props.user.id)
    ) {
      dispatch(listClients());
    }
    if (this.props.selectedClient !== props.selectedClient) {
      this.setDefaultDate();
      const { router: { query, setQuery } } = this.props;
      setQuery({ ...query, client: this.props.selectedClient });
    }
    // Keep the page-view beacon's user/client attribution current.
    if (
      (this.props.user && this.props.user.id) !== (props.user && props.user.id) ||
      this.props.selectedClient !== props.selectedClient
    ) {
      setUsageContext(this.props.user && this.props.user.id, this.props.selectedClient);
    }
    if (this.props.clients !== props.clients) {
      this.setDefaultDate();
    }
    if (this.props.aiLabelFilter?.open && !props.aiLabelFilter?.open) {
      const nsSlice = this.props.tagNamespaces?.[API_TAG_NAMESPACES_PROVIDERS_LIST];
      if (!nsSlice || !nsSlice.data) {
        this.props.dispatch(listProviderTagNamespaces(this.props.app.selectedClient));
      }
    }
  }

  render() {
    const { classes, user, clients, app, system, isMobile, aiLabelFilter } = this.props;
    const { openChat, clicked } = this.state;

    // pages where the icon must be hidden
    const HIDE_ICON_ON = [
      '/login',                 // exact page
      '/login_settings',        // admin screen
      '/print/',                // anything that starts with /print/
      '/activity',
      '/usage',                 // Analytics group (also covers /usage/compare)
      '/devrequests',
      '/upload',
      '/rvu_benchmarks',
      '/procedures',
      '/taxonomy',
      '/clients',
      '/users',
      '/announcements',
      '/custom_service_lines',
      '/testing'                // Testing tools (also covers /testing/locations)

    ];

    const { pathname } = this.props.router.location;

    let showSupportIcon = true;

    /* returns true if array item matches the start of pathname */
    showSupportIcon = !HIDE_ICON_ON.some((p) =>
      pathname.startsWith(p)
    );

    //if (!user?.is_admin) {
      
    //}


    if (!StoreUtil.isLoaded(clients)) {
      // don't load anything until clients are loaded
      return <LoadingView />;
    }

    let client = null;
    if (app.selectedClient && StoreUtil.isLoaded(clients)) {
      const data = StoreUtil.getData(clients);
      client = data.find((c) => c.id === parseInt(app.selectedClient));
    }

    if (
      StoreUtil.isLoaded(clients) &&
      StoreUtil.getData(clients).length === 0
    ) {
      return (
        <div className={classes.root}>
          <Header />
          <Card className={classes.cardContainer}>
            <CardHeader title='No Data Access' />
            <CardContent className={classes.cardContent}>
              <Typography variant='body2' color='error'>
                Your account does not have access to any clients. Please contact
                the Doctivity team and ask them to provide you access to your
                data.
              </Typography>
            </CardContent>
          </Card>
        </div>
      );
    }

    // don't load pages until the date range is initialized (ISO strings)
    const isLoading = !app.dateRange || !app.dateRange.start || !app.dateRange.end;

    let dialogs = [];
    if (system.dialogs) {
      dialogs = Object.keys(system.dialogs).map((id) => {
        const dialog = system.dialogs[id];
        if (dialog && dialog.props) {
          return dialog;
        }
        return null;
      });
    }

    return (
     <ChatBotContext.Provider value={{ openChat, clicked, setClicked: this.setClicked }}>
      <div className={classes.root}>
        <title>Doctivity</title>

        <AppNavigation 
          setOpenChat={this.registerOpenChat}
          setClicked={this.setClicked} 
        />

       {/*  GLOBAL SUPPORT ICON  */}
      {showSupportIcon && (
        <SupportIcon
          user={this.props.user}
          router={this.props.router}
          clientId={this.props.app.selectedClient}
          sx={{
            position: 'fixed',
            top : '2%',
            right: '30px',
            zIndex: 1500,          // higher than drawer/header
          }}
        />
        )}

        <main className={classes.content}>
          {isMobile && <div className={classes.appBarSpacer} />}
          <div className={classes.container}>
            {isLoading ? (
              <LoadingView />
            ) : (
              <Routes>
                {user.is_admin && (
                  <Route exact path='/users' element={<UsersPage />} authed />
                )}
                <Route path='/users/:id' element={<UserPage />} authed />
                {user.is_admin && (
                  <Route
                    exact
                    path='/clients'
                    element={<ClientsPage />}
                    authed
                  />
                )}
                {user.is_admin && (
                  <Route
                    exact
                    path='/activity'
                    element={<ActivityLogPage />}
                    authed
                  />
                )}
                {/* Dev requests raised from the Key AI chat. Intentionally has no side-menu
                    link (see AppNavigation.getAdminItems) — reached by URL only. The
                    is_admin wrapper is the actual access control, same as /activity. */}
                {user.is_admin && (
                  <Route
                    exact
                    path='/devrequests'
                    element={<DevRequestsPage />}
                    authed
                  />
                )}
                {user.is_admin && (
                  <Route
                    exact
                    path='/usage'
                    element={<UsageAnalyticsPage tab='usage' />}
                    authed
                  />
                )}
                {user.is_admin && (
                  <Route
                    exact
                    path='/usage/compare'
                    element={<UsageAnalyticsPage tab='compare' />}
                    authed
                  />
                )}
                {user.is_admin && (
                  <Route
                    exact
                    path='/login_settings'
                    element={<LoginSettingsPage />}
                    authed
                  />
                )}
                {user.is_admin && (
                  <Route
                    exact
                    path='/announcements'
                    element={<AnnouncementsPage />}
                    authed
                  />
                )}
                <Route
                  exact
                  path='/providers'
                   element={<ErrorBoundary><ProvidersPage /></ErrorBoundary>}
                  authed
                />
                 <Route
                  exact
                  path='/favorite_providers'
                  element={<FavoriteProvidersPage />}
                  authed
                />
                <Route
                  exact
                  path='/recent_providers'
                  element={<RecentProvidersPage />}
                  authed
                />
                <Route
                  exact
                  path='/referring_providers'
                  element={<ReferringProvidersPage />}
                  authed
                />
                <Route
                  exact
                  path='/affiliated_providers'
                  element={<AffiliatedProvidersPage />}
                  authed
                />
                <Route
                  path='/providers/:id'
                  element={<ProviderDetailPage />}
                  authed
                />
                <Route
                  path='/providers/:id/:tab'
                  element={<ProviderDetailPage />}
                  authed
                />
                {user.is_admin && (
                  <Route
                    exact
                    path='/taxonomy'
                    element={<TaxonomyPage />}
                    authed
                  />
                )}
                <Route
                  exact
                  path='/procedures'
                  element={<ProceduresPage />}
                  authed
                />
                <Route
                  exact
                  path='/query'
                  element={<QueryPage />}
                  authed
                />
                {user.is_admin && (
                  <Route exact path='/testing' element={<QAPage />} authed />
                )}
                {user.is_admin && (
                  <Route path='/testing/:tab' element={<QAPage />} authed />
                )}
                {/* The page was renamed QA -> Testing; these keep old /qa
                    bookmarks and links working. They MUST stay registered:
                    path='*' below renders the dashboard WITHOUT redirecting,
                    so an old /qa link would otherwise silently show the
                    dashboard at that URL. */}
                <Route exact path='/qa' element={<LegacyQaRedirect />} />
                <Route path='/qa/:tab' element={<LegacyQaRedirect />} />
                {user.is_admin && (
                  <Route exact path='/upload' element={<UploadPage />} authed />
                )}
                <Route
                  exact
                  path='/documents'
                  element={<DocumentsPage />}
                  authed
                />
                <Route
                  exact
                  path='/documents/data'
                  element={<DocumentsPage />}
                  authed
                />
                <Route
                  exact
                  path='/documents/tools'
                  element={<DocumentsPage />}
                  authed
                />

                <Route
                  exact
                  path='/notes/'
                  element={<ProviderCommentsPageContainer />}
                  authed
                />
                <Route
                  exact
                  path='/notes_report/'
                  element={<ProviderCommentsPageContainer tab='report' />}
                  authed
                />
                <Route
                  exact
                  path='/notes_admin/'
                  element={<ProviderCommentsPageContainer tab='admin' />}
                  authed
                />
                {/* Mileage sub-routes — must come before /notes/:id */}
                <Route
                  exact
                  path='/notes/mileage'
                  element={<ProviderCommentsPageContainer tab='tracking' />}
                  authed
                />
                <Route
                  path='/notes/mileage/:id'
                  element={<ProviderCommentsPageContainer tab='mileage_detail' />}
                  authed
                />
                {/* Stark sub-routes — must come before /notes/:id */}
                <Route
                  exact
                  path='/notes/stark'
                  element={<ProviderCommentsPageContainer tab='stark' />}
                  authed
                />
                <Route
                  path='/notes/stark/:id'
                  element={<ProviderCommentsPageContainer tab='stark_detail' />}
                  authed
                />
                <Route
                  path='/notes/:id'
                  // Unique key to force navigation to note tab even if it is loaded
                  element={<ProviderCommentsPageContainer tab='details' />}
                  authed
                />
                <Route
                  exact
                  path='/rvu_benchmarks'
                  element={<RVUBenchmarksPage />}
                  authed
                />
                <Route exact path='/visits' element={<VisitsPage />} authed />
                <Route
                  path='/flow/:direction/:providerId'
                  element={<ReferralFlowPage />}
                  authed
                />
                <Route
                  path='/flow/:direction'
                  element={<ReferralFlowPage />}
                  authed
                />
                <Route path='/flow' element={<ReferralFlowPage />} authed />
                {client.has_claims_access && (
                  <Route exact path='/claims' element={<ClaimsTabs />} authed />
                )}
                <Route 
                  exact 
                  path='/custom_service_lines' 
                  element={<CustomServiceLinesPage />} 
                  authed 
                />                
                <Route
                  exact
                  path='/organizations'
                  element={<OrganizationsPage />}
                  authed
                />
               <Route
                  exact
                  path='/favorite_organizations'
                  element={<FavoriteOrganizationsPage />}
                  authed
                />
                <Route
                  exact
                  path='/recent_organizations'
                  element={<RecentOrganizationsPage />}
                  authed
                />
                <Route
                  exact
                  path='/affiliated_organizations'
                  element={<AffiliatedOrganizationsPage />}
                  authed
                />
                <Route
                  exact
                  path='/patient_migration'
                  element={<PatientMigrationPage />}
                  authed
                />
                <Route
                  path='/organizations/:organizationId'
                  element={<OrganizationDetailPage />}
                  authed
                />
                <Route
                  path='/organizations/:organizationId/:tab'
                  element={<OrganizationDetailPage />}
                  authed
                />
                <Route
                  exact
                  path='/locations'
                  element={<LocationsPage />}
                  authed
                />
                <Route
                  exact
                  path='/recent_locations'
                  element={<RecentLocationsPage />}
                  authed
                />
                <Route
                  exact
                  path='/location_changes'
                  element={<LocationChangesPage />}
                  authed
                />      
                <Route
                  exact
                  path='/locations_map'
                  element={<LocationsMapPage />}
                  authed
                />                     
                <Route
                  exact
                  path='/favorite_locations'
                  element={<FavoriteLocationsPage />}
                  authed
                />
                <Route
                  path='/locations/:id'
                  element={<LocationDetailPage />}
                  authed
                />
                <Route
                  path='/locations/:id/:tab'
                  element={<LocationDetailPage />}
                  authed
                />
                <Route
                  exact
                  path='/contacts'
                  element={<ContactsPage />}
                  authed
                />
                <Route
                  path='/contacts/:contactId'
                  element={<ContactDetailPage />}
                  authed
                />
                <Route
                  path='/contacts/:contactId/:tab'
                  element={<ContactDetailPage />}
                  authed
                />
                 <Route
                  exact
                  path='/favorite_contacts'
                  element={<FavoriteContactsPage />}
                  authed
                />
                <Route
                  exact
                  path='/recent_contacts'
                  element={<RecentContactsPage />}
                  authed
                />
                <Route
                  exact
                  path='/reports'
                  element={<ReportBuilderPage />}
                  authed
                />{/* admin-gated inside the component so a direct URL always routes */}
                <Route
                  exact
                  path='/labels'
                  element={<TagDetailsPage />}
                  authed
                />
                <Route
                  path='/labels/:tagId'
                  element={<TagDetailsPage />}
                  authed
                />
                <Route
                  path='/labels/:tagId/:tab'
                  element={<TagDetailsPage />}
                  authed
                />
               
                <Route
                  exact
                  path='/label_types/'
                  element={<TagNamespacesPage />}
                  authed
                />
                <Route
                  exact
                  path='/label_types/:tagNamespaceId'
                  element={<LabelTypesPage />}
                  authed
                />
                {/* A notification has its own address so it can be linked to
                    directly — shared, or used as the "open it in Doctivity" link
                    in an email. The bell on the dashboard is still how most
                    people get here; this is a second door to the same thing.
                    These MUST stay registered: AppRoot's path='*' below renders
                    the dashboard WITHOUT redirecting, so an unregistered
                    /notifications/123 would silently show the dashboard at that
                    URL rather than a not-found. (Order doesn't matter —
                    react-router v7 ranks by specificity, and the splat always
                    ranks last.) */}
                <Route
                  exact
                  path='/notifications'
                  element={<NotificationsPageContainer />}
                  authed
                />
                <Route
                  exact
                  path='/notifications/:id'
                  element={<NotificationsPageContainer tab='detail' />}
                  authed
                />
                <Route
                  exact
                  path='/dashboard'
                  element={<DashboardV2Page tab='providers' />}
                  authed
                />
                <Route
                  exact
                  path='/dashboard/providers'
                  element={<DashboardV2Page tab='providers' />}
                  authed
                />
                <Route
                  exact
                  path='/dashboard/locations'
                  element={<DashboardV2Page tab='locations' />}
                  authed
                />
                <Route
                  exact
                  path='/dashboard/contacts'
                  element={<DashboardV2Page tab='contacts' />}
                  authed
                />
                <Route path='*'
                  element={<DashboardV2Page tab='providers' />}
                />
              </Routes>
            )}
          </div>
          <Footer />
          <ActivitySnackbar />
          {system.confirmationDialog && (
            <ConfirmationDialog
              open={system.confirmationDialog != null}
              onCancel={
                system.confirmationDialog.onCancel
                  ? () => {
                      system.confirmationDialog.onCancel();
                      this.props.dispatch(hideConfirmationDialog());
                    }
                  : null
              }
              onClose={() => {
                this.props.dispatch(hideConfirmationDialog());
              }}
              onConfirm={() => {
                if (typeof system.confirmationDialog.onConfirm === 'function') {
                  system.confirmationDialog.onConfirm();
                }
                this.props.dispatch(hideConfirmationDialog());
              }}
              title={system.confirmationDialog.title}
              content={system.confirmationDialog.content}
              confirmLabel={system.confirmationDialog.confirmLabel}
              confirmVariant={system.confirmationDialog.confirmVariant}
              cancelLabel={system.confirmationDialog.cancelLabel}
            />
          )}
          {dialogs}
        </main>
      </div>
      <Dialog
        open={!!aiLabelFilter?.open && this.props.router?.location?.pathname?.startsWith('/labels')}
        maxWidth="lg"
        fullWidth
        onClose={() => this.props.dispatch(closeAiLabelFilter())}
        PaperProps={{ sx: { height: '85vh' } }}
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}>
          <AiProviderFilter
            existingNamespaces={
              StoreUtil.getData(
                this.props.tagNamespaces?.[API_TAG_NAMESPACES_PROVIDERS_LIST]
              ) || []
            }
            onClose={() => this.props.dispatch(closeAiLabelFilter())}
          />
        </DialogContent>
      </Dialog>
        </ChatBotContext.Provider>
    );
  }

  getStartDate = () => {
    const { selectedClient, clients } = this.props;

    let startDate = null;
    let client = null;
    const clientData = StoreUtil.getData(clients);
    if (selectedClient) {
      client = clientData?.find((c) => c.id === parseInt(selectedClient));
    }

    if (client) {
      if (client.most_recent_date) {
        startDate = new Date(client.most_recent_date);
      }
    }

    if (!startDate) {
      startDate = subMonths(new Date(), 1);
    }
    return startDate;
  };

  setDefaultDate = () => {
    const { dispatch, app } = this.props;

    let dateRange = app.dateRange;
    if (!dateRange) {
      const startDate = this.getStartDate();

      const lastMonth = endOfMonth(startDate);
      // startOfDay: without it the range began at 23:59:59.999 and dropped the first day.
      const lastYear = startOfDay(subYears(addDays(lastMonth, 1), 1));
      const last365 = 'Last 365 Days';

      // set to default, nothing set yet
      dateRange = {
        start: lastYear.toISOString(),
        end: lastMonth.toISOString(),
        name: last365,
        preset: 3,
      };
    }

    dispatch(setDateRange(dateRange));
  };
}

AppRoot.propTypes = {
  router: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedClient: PropTypes.number,
  user: PropTypes.object,
  clients: PropTypes.object,
  app: PropTypes.object,
  system: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    app: state.app,
    user: state.user,
    system: state.system,
    selectedClient: state.app.selectedClient,
    clients: StoreUtil.get(state.clients, StoreUtil.COMMON_LIST),
    tagNamespaces: state.tagNamespaces,
    aiLabelFilter: state.app.aiLabelFilter,
  };
}
//const styledApp = withIsMobile(withStyles(styles)(AppRoot));
//const connected = connect(mapStateToProps)(styledApp);
//const routed = withRouter(withClientQuery(connected));
//export { routed as AppRoot };

const AppRootWithMobile = withIsMobile(AppRoot);
const AppRootStyled = withStyles(styles)(AppRootWithMobile);
const AppRootConnected = connect(mapStateToProps)(AppRootStyled);
const AppRootRouted = withRouter(withClientQuery(AppRootConnected));
export { AppRootRouted as AppRoot };